// Shell plochy: tapeta, ikony (složka Plocha z úložiště), hlavní panel (Start, připnuté, běžící okna, oznamovací oblast,
// hodiny, uživatel), Start, kontextová menu, přizpůsobení. Používá jen rozhraní jádra dostupná každé aplikaci.
import { core, api, action, setToken, upload, ApiError, getToken } from '../api';
import { canManage, canWrite } from '../contract';
import type { AppInfo, SessionBoot, StorageItem, Shortcut, PageBoot } from '../contract';
import { SHORTCUT_MIME } from '../contract';
import { h, clear, debounce, onOutsideClick, isTyping } from '../dom';
import { t, tk, fmtTime, currentLang } from '../i18n';
import { icon, fileIcon, IWAN_MARK } from '../icons';
import { downloadItem } from './download';
import { svg } from '../dom';
import { events } from '../events';
import { WindowManager, type LayoutEntry } from '../wm/manager';
import { IwMenu, contextMenu, type MenuItem } from '../components/menu';
import { toast, toastHistory, onToast, toastError } from '../components/toast';
import { confirm, prompt, alert, dialog } from '../components/dialog';
import { applyTheme, applyFontScale, applyWallpaper, currentTheme, currentFontScale, savePref, type ThemePref } from '../theme';
import { lockScreen } from './lock';
import { aiInfo, setAiOptOut } from '../components/ai';
import { pickFile, pickFolder, configurePicker } from './picker';
import { collectFromDrop, pickNative, dropHighlight, runExport } from './dropzone';
import { uploadFiles as queueUpload, configureUploads } from './uploads';
import { setRibbonCollapsedState, watchRibbonCollapsed } from '../components/ribbon';
import { fileClipboard, setFileClipboard, pasteFileClipboard } from './clipboard';
import { loadLabels, labelColor, type CoreLabel } from '../labels';

export interface DesktopHandlers {
  logout(forget: boolean): Promise<void>;
  switchUser(): void;
}

export class Desktop {
  readonly root: HTMLElement;
  readonly wm: WindowManager;
  private iconsLayer = h('div', { class: 'desktop-icons', role: 'list' });
  private taskbar = h('div', { class: 'taskbar' });
  private running = h('div', { class: 'tb-running' });
  private tray = h('div', { class: 'tb-tray' });
  private clock = h('button', { type: 'button', class: 'tb-clock' });
  private connIcon = h('span', { class: 'tb-conn ok', title: t('desktop.shell.connected'), hidden: true }, icon('wifi'));
  private startMenu: HTMLElement | null = null;
  private desktopItems: StorageItem[] = [];
  private iconSize: 'small' | 'medium' | 'large' = 'medium';
  private groupTaskbar = true;
  private selectMode = false; // dotyk: klepnutí přepíná výběr, zaškrtávátka na ikonách
  private iconPos: Record<string, [number, number]> = {};
  private iconSort: 'name' | 'type' | 'date' | null = null;
  private swallowClick = false;
  private pinned: string[] = [];
  private idleTimer: number | undefined;
  private lastActivity = Date.now();
  private locked = false;
  private unsubscribe: (() => void) | null = null;

  constructor(
    private boot: SessionBoot,
    private page: PageBoot,
    container: HTMLElement,
    private handlers: DesktopHandlers,
  ) {
    const wallpaper = h('div', { class: 'desktop-wallpaper' });
    const area = h('div', { class: 'desktop-area' }, wallpaper, this.iconsLayer);
    this.root = h('div', { class: 'desktop' }, area, this.taskbar);
    container.replaceChildren(this.root);
    this.wm = new WindowManager(area);
    configurePicker(this.wm);
    configureUploads(this.wm);
    this.wm.onChange = () => this.renderRunning();
    this.wm.onFocusChange = () => this.renderRunning();
    (window as unknown as { iwanPrefs: Record<string, unknown>; iwanDesktop: Desktop }).iwanPrefs = boot.prefs;
    (window as unknown as { iwanDesktop: Desktop }).iwanDesktop = this;
    this.applyPrefs();
    this.buildTaskbar();
    contextMenu(area, (e) => ((e.target as HTMLElement).closest('.desktop-icon, iw-window, .taskbar') ? null : this.desktopMenu()));
    this.iconsLayer.addEventListener('click', (e) => {
      if (this.swallowClick) {
        this.swallowClick = false;
        return;
      }
      if (e.target === this.iconsLayer) this.clearIconSelection();
    });
    this.installDrop(area);
    // 4) kde nemáme vlastní nabídku, neukazovat ani nativní nabídku prohlížeče (kromě textových polí – kopírovat/vložit)
    this.root.addEventListener('contextmenu', (e) => {
      if (!isTyping(e.target)) e.preventDefault();
    });
    void this.loadDesktopItems();
    this.unsubscribe = events.subscribe(['core.storage:' + boot.desktopFolderId], () => void this.loadDesktopItems(), { onReset: () => void this.loadDesktopItems() });
    events.onConnection = (online) => this.setConnection(online);
    events.onLocked = (locked) => {
      if (locked && !this.locked) void this.lock(false);
    };
    this.installIdle();
    document.addEventListener('keydown', (e) => this.onKey(e));
    onToast(() => this.renderTray());
    void this.restoreLayout();
  }

  // ---------------------------------------------------------------- preference

  private applyPrefs(): void {
    const p = this.boot.prefs;
    applyTheme((p['theme'] as ThemePref) ?? currentTheme());
    if (typeof p['fontScale'] === 'number') applyFontScale(p['fontScale']);
    const wp = p['wallpaper'] as { url?: string | null; color?: string | null; fileId?: string; system?: string } | null | undefined;
    applyWallpaper(wp && typeof wp === 'object' ? { color: wp.color ?? null, url: wp.system ? this.systemWallpaperUrl(wp.system) : null } : null);
    if (wp && typeof wp === 'object' && typeof wp.fileId === 'string') {
      // podepsaný odkaz vyprší → při startu si vyžádáme nový podle id souboru
      void core<{ item: StorageItem }>('storage', 'get', { id: wp.fileId })
        .then((r) => r.item.url && applyWallpaper({ url: this.page.prefix + r.item.url, color: wp.color ?? null }))
        .catch(() => undefined);
    }
    if (p['taskbarTop'] === true) this.root.classList.add('taskbar-top');
    if (p['taskbarCenter'] === true) this.root.classList.add('taskbar-center');
    if (p['iconSize'] === 'small' || p['iconSize'] === 'large') this.iconSize = p['iconSize'];
    if (p['taskbarGroup'] === false) this.groupTaskbar = false;
    if (p['ribbonCollapsed'] !== undefined) setRibbonCollapsedState(p['ribbonCollapsed'], true); // po aplikacích (dříve jedno true/false)
    watchRibbonCollapsed((state) => void savePref('ribbonCollapsed', state));
    if (p['desktopIcons'] && typeof p['desktopIcons'] === 'object') this.iconPos = p['desktopIcons'] as Record<string, [number, number]>;
    if (p['desktopSort'] === 'name' || p['desktopSort'] === 'type' || p['desktopSort'] === 'date') this.iconSort = p['desktopSort'];
    if (Array.isArray(p['pinned'])) this.pinned = (p['pinned'] as unknown[]).filter((x): x is string => typeof x === 'string');
  }

  // ---------------------------------------------------------------- plocha

  private async loadDesktopItems(): Promise<void> {
    try {
      const res = await core<{ items: StorageItem[] }>('storage', 'children', { id: this.boot.desktopFolderId });
      this.desktopItems = res.items;
      this.renderIcons();
      if (!this.labelsWatched) this.refreshLabels(); // barvy štítků a nabídka Štítky (načtené dřív, než ji někdo otevře)
    } catch (e) {
      console.warn('desktop items', e);
    }
  }

  private cellSize(): { w: number; h: number; gap: number; pad: number } {
    const s = this.iconSize;
    return { w: s === 'small' ? 76 : s === 'large' ? 112 : 92, h: s === 'small' ? 72 : s === 'large' ? 120 : 96, gap: 6, pad: 12 };
  }

  private gridRows(): number {
    const { h: ch, gap, pad } = this.cellSize();
    return Math.max(1, Math.floor((this.iconsLayer.clientHeight - pad * 2 + gap) / (ch + gap)));
  }

  private cellAt(x: number, y: number): [number, number] {
    const { w, h: ch, gap, pad } = this.cellSize();
    const r = this.iconsLayer.getBoundingClientRect();
    return [Math.max(0, Math.floor((x - r.left - pad) / (w + gap))), Math.max(0, Math.min(this.gridRows() - 1, Math.floor((y - r.top - pad) / (ch + gap))))];
  }

  /** Každé ikoně přidělí buňku: uložené pozice mají přednost, ostatní zaplní volná místa po sloupcích. */
  private layoutIcons(): Map<string, [number, number]> {
    const rows = this.gridRows();
    const used = new Set<string>();
    const out = new Map<string, [number, number]>();
    const items = this.sortedItems();
    for (const it of items) {
      const p = this.iconPos[it.id];
      if (p && !used.has(p.join(','))) {
        const cell: [number, number] = [p[0], Math.min(p[1], rows - 1)];
        used.add(cell.join(','));
        out.set(it.id, cell);
      }
    }
    let c = 0;
    let r = 0;
    for (const it of items) {
      if (out.has(it.id)) continue;
      while (used.has(c + ',' + r)) {
        r++;
        if (r >= rows) {
          r = 0;
          c++;
        }
      }
      out.set(it.id, [c, r]);
      used.add(c + ',' + r);
    }
    return out;
  }

  private sortedItems(): StorageItem[] {
    const list = [...this.desktopItems];
    const s = this.iconSort;
    if (!s) return list;
    return list.sort((a, b) => {
      if (s === 'type') return (a.kind + (a.mime ?? '')).localeCompare(b.kind + (b.mime ?? '')) || a.name.localeCompare(b.name);
      if (s === 'date') return (b.updatedAt ?? b.createdAt).localeCompare(a.updatedAt ?? a.createdAt);
      return a.name.localeCompare(b.name, undefined, { numeric: true });
    });
  }

  private moveIcon(id: string, cell: [number, number]): void {
    const layout = this.layoutIcons();
    const rows = this.gridRows();
    const occupant = Array.from(layout.entries()).find(([k, v]) => k !== id && v[0] === cell[0] && v[1] === cell[1]);
    let target = cell;
    if (occupant) {
      // obsazeno: najít další volnou buňku pod ní / v dalším sloupci
      const used = new Set(Array.from(layout.entries()).filter(([k]) => k !== id).map(([, v]) => v.join(',')));
      let [c, r] = cell;
      while (used.has(c + ',' + r)) {
        r++;
        if (r >= rows) {
          r = 0;
          c++;
        }
      }
      target = [c, r];
    }
    for (const [k, v] of layout) if (k !== id) this.iconPos[k] = v; // zafixovat ostatní, aby se neposunuly
    this.iconPos[id] = target;
    this.iconSort = null;
    void savePref('desktopIcons', this.iconPos);
    void savePref('desktopSort', null);
    this.renderIcons();
  }

  private renderIcons(): void {
    clear(this.iconsLayer);
    this.iconsLayer.dataset['size'] = this.iconSize;
    const layout = this.layoutIcons();
    for (const item of this.desktopItems) {
      const cell = layout.get(item.id) ?? [0, 0];
      const el = h(
        'button',
        { type: 'button', class: 'desktop-icon', role: 'listitem', dataset: { id: item.id }, draggable: 'true', title: item.name },
        h('span', { class: 'di-icon' }, this.itemIcon(item), item.shared ? icon('users', 'icon di-shared') : null, this.selectMode ? h('span', { class: 'sel-check di-check' }, icon('check')) : null),
        h('span', { class: 'di-label' }, item.name.replace(/\.iwan$/i, '')),
        item.tags?.length ? h('span', { class: 'di-tags', title: item.tags.join(', ') }, item.tags.slice(0, 5).map((n) => h('span', { class: 'di-tag', style: { '--tag': labelColor(this.labelList, n) ?? '#888' } }))) : null,
      );
      el.style.gridColumn = String(cell[0] + 1);
      el.style.gridRow = String(cell[1] + 1);
      el.addEventListener('click', (e) => {
        if (!e.ctrlKey && !this.selectMode) this.clearIconSelection();
        el.classList.toggle('selected', e.ctrlKey || this.selectMode ? !el.classList.contains('selected') : true);
      });
      el.addEventListener('dblclick', () => void this.openItem(item));
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') void this.openItem(item);
        if (e.key === 'F2') void this.renameItem(item);
        if (e.key === 'Delete') void this.deleteItems([item]);
      });
      el.addEventListener('dragstart', (e) => {
        if (!el.classList.contains('selected')) {
          this.clearIconSelection();
          el.classList.add('selected');
        }
        e.dataTransfer?.setData('application/x-iwan-items', JSON.stringify(this.selectedItems().map((i) => i.id)));
        e.dataTransfer?.setData('application/x-iwan-desktop', item.id);
        e.dataTransfer?.setData('text/plain', item.name);
      });
      contextMenu(el, () => {
        this.clearIconSelection();
        el.classList.add('selected');
        return this.itemMenu(item);
      });
      this.iconsLayer.appendChild(el);
    }
  }

  /** globální štítky (jádro): barvy teček u ikon, nabídka Štítky */
  private labelList: CoreLabel[] = [];
  private labelsWatched = false;

  private refreshLabels(fresh = false): void {
    if (!this.labelsWatched) {
      this.labelsWatched = true;
      events.subscribe(['core.labels'], () => this.refreshLabels(true), {});
    }
    void loadLabels(fresh).then((l) => { this.labelList = l; this.renderIcons(); });
  }

  /** Štítky ikon na ploše – stejná nabídka jako v Souborech (zaškrtnuté = mají všechny vybrané). */
  private tagMenu(sel: StorageItem[]): MenuItem[] {
    const has = (n: string): boolean => sel.every((i) => (i.tags ?? []).some((x) => x.toLowerCase() === n.toLowerCase()));
    const toggle = async (name: string): Promise<void> => {
      const all = has(name);
      try {
        await core('storage', 'tags', { ids: sel.map((i) => i.id), add: all ? [] : [name], remove: all ? [name] : [] });
        for (const i of sel) i.tags = all ? (i.tags ?? []).filter((x) => x.toLowerCase() !== name.toLowerCase()) : Array.from(new Set([...(i.tags ?? []), name]));
        this.renderIcons();
      } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
    };
    return [
      ...this.labelList.map((l): MenuItem => ({ label: l.name, color: l.color, checked: has(l.name), onSelect: () => void toggle(l.name) })),
      { separator: true, label: '' },
      { label: t('core.labels.manage'), icon: 'settings', onSelect: () => void this.wm.open({ app: 'desktop', window: 'stitky' }) },
    ];
  }

  private itemIcon(item: StorageItem): Node {
    if (item.isShortcut) {
      return h('span', { class: 'di-shortcut' }, icon(this.shortcutIconFor(item) ?? 'app'), h('span', { class: 'di-arrow' }, icon('shortcut')));
    }
    if (item.kind === 'file' && item.mime?.startsWith('image/') && item.url) return h('img', { src: this.page.prefix + item.url + '&w=128', alt: '', class: 'di-thumb', loading: 'lazy' });
    return icon(fileIcon(item.mime, false, item.kind === 'folder'));
  }

  private shortcutCache = new Map<string, Shortcut>();

  private shortcutIconFor(item: StorageItem): string | null {
    const sc = this.shortcutCache.get(item.id);
    if (sc) return sc.icon ?? this.boot.apps.find((a) => a.id === sc.app)?.icon ?? 'app';
    void core<{ content: string }>('storage', 'text', { id: item.id })
      .then((r) => {
        this.shortcutCache.set(item.id, JSON.parse(r.content) as Shortcut);
        this.renderIcons();
      })
      .catch(() => undefined);
    return null;
  }

  private clearIconSelection(): void {
    this.iconsLayer.querySelectorAll('.selected').forEach((el) => el.classList.remove('selected'));
  }

  private selectedItems(): StorageItem[] {
    const ids = new Set(Array.from(this.iconsLayer.querySelectorAll<HTMLElement>('.desktop-icon.selected')).map((el) => el.dataset['id']));
    return this.desktopItems.filter((i) => ids.has(i.id));
  }

  async openItem(item: StorageItem): Promise<void> {
    if (item.kind === 'folder') {
      await this.wm.open({ app: 'soubory', window: 'pruzkumnik', params: { folder: item.id } });
      return;
    }
    if (item.isShortcut || item.mime === SHORTCUT_MIME) {
      try {
        const r = await core<{ content: string }>('storage', 'text', { id: item.id });
        const sc = JSON.parse(r.content) as Shortcut;
        await this.openWindow(sc.app, sc.window, sc.params ?? {});
      } catch (e) {
        toast(tk((e as ApiError).message ?? 'core.err.internal'), 'error');
      }
      return;
    }
    const target = this.appForMime(item.mime);
    if (target) await this.wm.open({ app: target.app, window: target.window, params: { id: item.id } });
    else void downloadItem(item, this.page.prefix);
  }

  appForMime(mime: string | null): { app: string; window: string } | null {
    if (!mime) return null;
    for (const app of this.boot.apps) {
      for (const [pattern, win] of Object.entries(app.fileTypes)) {
        if (pattern === mime || (pattern.endsWith('/*') && mime.startsWith(pattern.slice(0, -1)))) return { app: app.id, window: win };
      }
    }
    return null;
  }

  openWithMenu(item: StorageItem): MenuItem[] {
    const out: MenuItem[] = [];
    for (const app of this.boot.apps) {
      for (const [, win] of Object.entries(app.fileTypes)) {
        if (!out.some((o) => o.id === app.id)) out.push({ id: app.id, label: tk(app.name), icon: app.icon, onSelect: () => void this.wm.open({ app: app.id, window: win, params: { id: item.id } }) });
      }
    }
    return out;
  }

  async openWindow(app: string, window: string, params: Record<string, unknown> = {}, newInstance = false): Promise<void> {
    const appInfo = this.boot.apps.find((a) => a.id === app);
    const winInfo = appInfo?.windows.find((w) => w.id === window);
    if (!appInfo || !winInfo) {
      toast(t('core.err.app_not_found'), 'error');
      return;
    }
    await this.wm.open({ app, window, params, newInstance });
  }

  private itemMenu(item: StorageItem): MenuItem[] {
    const sel = this.selectedItems();
    const items: MenuItem[] = [{ label: t('desktop.shell.open'), icon: 'folder-open', onSelect: () => void this.openItem(item) }];
    if (item.kind === 'file' && !item.isShortcut) {
      const ow = this.openWithMenu(item);
      if (ow.length) items.push({ label: t('desktop.shell.open_with'), icon: 'app', children: ow });
      items.push({ label: t('soubory.download'), icon: 'download', onSelect: () => item.url && window.open(this.page.prefix + item.url + '&dl=1', '_blank') });
    }
    items.push({ separator: true, label: '' });
    const ids = (sel.length > 1 ? sel : [item]).map((i) => i.id);
    items.push({ label: t('soubory.cut'), icon: 'scissors', shortcut: 'Ctrl+X', onSelect: () => setFileClipboard(ids, true) });
    items.push({ label: t('soubory.copy'), icon: 'copy', shortcut: 'Ctrl+C', onSelect: () => setFileClipboard(ids, false) });
    items.push({ label: t('soubory.move_to'), icon: 'folder-open', onSelect: () => void this.moveTo(ids) });
    items.push({ separator: true, label: '' });
    items.push({ label: t('desktop.shell.rename'), icon: 'edit', shortcut: 'F2', onSelect: () => void this.renameItem(item) });
    items.push({ label: t('desktop.shell.delete'), icon: 'trash', shortcut: 'Delete', danger: true, onSelect: () => void this.deleteItems(sel.length > 1 ? sel : [item]) });
    items.push({ separator: true, label: '' });
    const tagSel = sel.length > 1 ? sel : [item];
    if (!this.labelsWatched) this.refreshLabels();
    if (tagSel.every((i) => !i.isShortcut && canWrite(i.access))) items.push({ label: t('soubory.labels'), icon: 'tag', children: this.tagMenu(tagSel) });
    if (!item.isShortcut && ((item.space === 'user' && canManage(item.access)) || canWrite(item.access))) items.push({ label: t('desktop.shell.share'), icon: 'users', onSelect: () => void this.wm.open({ app: 'desktop', window: 'sdileni', params: { type: 'storage', id: item.id, title: item.name }, modal: true }) });
    items.push({ label: t('desktop.shell.properties'), icon: 'info', onSelect: () => void this.wm.open({ app: 'desktop', window: 'vlastnosti', params: { id: item.id } }) });
    return items;
  }

  /** Přesunout do… – výběr cílové složky dialogem (na dotyku náhrada za přetažení). */
  private async moveTo(ids: string[]): Promise<void> {
    const target = await pickFolder();
    if (!target) return;
    try {
      await core('storage', 'move', { ids, target: target.id });
      toast(t('soubory.moved_to', { name: target.name }), 'ok');
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  private async renameItem(item: StorageItem): Promise<void> {
    const name = await prompt(t('desktop.shell.rename'), item.name);
    if (name === null || name === item.name) return;
    try {
      await core('storage', 'rename', { id: item.id, name });
      events.kick();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  private async deleteItems(items: StorageItem[]): Promise<void> {
    if (!items.length || !(await confirm(t('core.cmd.confirm_delete', { count: items.length }), { danger: true, okLabel: t('desktop.shell.delete') }))) return;
    try {
      await core('storage', 'trash', { ids: items.map((i) => i.id) });
      events.kick();
      toast(t('soubory.deleted'), 'ok');
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  private desktopMenu(): MenuItem[] {
    return [
      {
        label: t('core.ui.select_mode'),
        icon: 'check-square',
        checked: this.selectMode,
        onSelect: () => {
          this.selectMode = !this.selectMode;
          this.renderIcons();
        },
      },
      {
        label: t('desktop.shell.view'),
        icon: 'eye',
        children: (['small', 'medium', 'large'] as const).map((s) => ({
          label: t('desktop.shell.' + s),
          checked: this.iconSize === s,
          onSelect: () => {
            this.iconSize = s;
            this.renderIcons();
            void savePref('iconSize', s);
          },
        })),
      },
      {
        label: t('desktop.shell.sort'),
        icon: 'sort',
        children: (['name', 'type', 'date'] as const).map((s) => ({
          label: t('desktop.shell.sort_' + s),
          checked: this.iconSort === s,
          onSelect: () => {
            this.iconSort = s;
            this.iconPos = {};
            void savePref('desktopSort', s);
            void savePref('desktopIcons', {});
            this.renderIcons();
          },
        })),
      },
      {
        label: t('desktop.shell.align_grid'),
        icon: 'grid',
        onSelect: () => {
          this.iconPos = {};
          void savePref('desktopIcons', {});
          this.renderIcons();
        },
      },
      { label: t('desktop.shell.refresh'), icon: 'refresh', shortcut: 'F5', onSelect: () => void this.loadDesktopItems() },
      { separator: true, label: '' },
      {
        label: t('desktop.shell.new'),
        icon: 'plus',
        children: [
          { label: t('desktop.shell.new_folder'), icon: 'folder', onSelect: () => void this.newFolder() },
          { label: t('desktop.shell.new_text'), icon: 'file-text', onSelect: () => void this.newText() },
          { label: t('desktop.shell.new_shortcut'), icon: 'shortcut', onSelect: () => void this.newShortcut() },
        ],
      },
      { label: t('desktop.shell.upload'), icon: 'upload', onSelect: () => this.pickUpload(false) },
      { label: t('soubory.upload_folder'), icon: 'folder', onSelect: () => this.pickUpload(true) },
      { label: t('soubory.paste'), icon: 'clipboard', shortcut: 'Ctrl+V', disabled: !fileClipboard(), onSelect: () => void pasteFileClipboard(this.boot.desktopFolderId) },
      { separator: true, label: '' },
      { label: t('desktop.shell.personalize'), icon: 'palette', onSelect: () => this.personalize() },
      { label: t('desktop.shell.task_manager'), icon: 'activity', onSelect: () => void this.openWindow('desktop', 'spravce_uloh') },
      { label: t('desktop.shell.about'), icon: 'info', onSelect: () => void this.openWindow('desktop', 'o_systemu') },
    ];
  }

  private async newFolder(): Promise<void> {
    const name = await prompt(t('desktop.shell.folder_name'), t('soubory.new_folder'));
    if (name === null) return;
    await core('storage', 'mkdir', { parent: this.boot.desktopFolderId, name }).catch((e: ApiError) => toastError(e));
    events.kick();
  }

  private async newText(): Promise<void> {
    const name = await prompt(t('desktop.shell.file_name'), t('soubory.new_text'));
    if (name === null) return;
    const res = await core<{ item: StorageItem }>('storage', 'textfile', { parent: this.boot.desktopFolderId, name, content: '', mime: 'text/plain' }).catch((e: ApiError) => {
      toastError(e);
      return null;
    });
    events.kick();
    if (res) void this.wm.open({ app: 'prohlizec', window: 'zobrazit', params: { id: res.item.id } });
  }

  private async newShortcut(): Promise<void> {
    // výběr aplikace a okna
    const options: { app: AppInfo; win: AppInfo['windows'][number] }[] = [];
    for (const app of this.boot.apps) for (const w of app.windows) if (w.inStart) options.push({ app, win: w });
    const sel = h('select', { class: 'input' }, options.map((o, i) => h('option', { value: String(i) }, `${tk(o.app.name)} – ${tk(o.win.title)}`)));
    const nameInput = h('input', { class: 'input', type: 'text' });
    sel.addEventListener('change', () => {
      const o = options[Number(sel.value)];
      if (o) nameInput.value = tk(o.win.title);
    });
    nameInput.value = options[0] ? tk(options[0].win.title) : '';
    const { dialog } = await import('../components/dialog');
    const r = await dialog({
      title: t('desktop.shell.new_shortcut'),
      message: h('div', null, h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('desktop.shell.apps')), sel), h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('desktop.shell.shortcut_name')), nameInput)),
      buttons: [
        { id: 'cancel', label: t('core.ui.cancel') },
        { id: 'ok', label: t('core.ui.ok'), primary: true },
      ],
    });
    if (r.id !== 'ok') return;
    const o = options[Number(sel.value)];
    if (!o) return;
    await this.createShortcut(o.app.id, o.win.id, {}, nameInput.value.trim() || tk(o.win.title), o.win.icon);
  }

  /** „Vytvořit zástupce“ – dostupné každému oknu zdarma (zná svůj popis). */
  async createShortcut(app: string, window: string, params: Record<string, unknown>, name: string, iconName?: string): Promise<void> {
    try {
      await core('storage', 'shortcut', { parent: this.boot.desktopFolderId, app, window, params, name: name + '.iwan', icon: iconName ?? null });
      events.kick();
      toast(t('desktop.shell.shortcut_created'), 'ok');
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  private pickUpload(folder: boolean): void {
    void pickNative({ folder, multiple: true }).then((c) => c && this.uploadFiles(c.files, this.boot.desktopFolderId, c.paths));
  }

  async uploadFiles(files: FileList | File[], parent: string, paths?: string[], dirs?: string[]): Promise<void> {
    await queueUpload(parent, files, paths, false, dirs);
    events.kick();
  }

  private installDrop(area: HTMLElement): void {
    area.addEventListener('dragover', (e) => {
      if ((e.target as HTMLElement).closest('iw-window')) return;
      if (e.dataTransfer?.types.includes('application/x-iwan-desktop')) {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        return;
      }
      if (e.dataTransfer?.types.includes('Files') || e.dataTransfer?.types.includes('application/x-iwan-export')) {
        e.preventDefault();
      }
    });
    dropHighlight(area, (e) => Boolean(e.dataTransfer?.types.includes('Files') || e.dataTransfer?.types.includes('application/x-iwan-export')) && !(e.target as HTMLElement).closest('iw-window'));
    // výběr více ikon rámečkem
    area.addEventListener('pointerdown', (e) => {
      // rámeček jen myší/perem – na dotyku se plete s posunem prstem; výběr více položek řeší dlouhý stisk
      if (e.button !== 0 || e.target !== this.iconsLayer || e.pointerType === 'touch') return;
      const start = { x: e.clientX, y: e.clientY };
      const box = h('div', { class: 'marquee' });
      let active = false;
      const move = (ev: PointerEvent) => {
        if (!active && Math.abs(ev.clientX - start.x) + Math.abs(ev.clientY - start.y) < 4) return;
        if (!active) {
          active = true;
          area.appendChild(box);
          if (!ev.ctrlKey) this.clearIconSelection();
        }
        const r = area.getBoundingClientRect();
        const x1 = Math.min(start.x, ev.clientX) - r.left;
        const y1 = Math.min(start.y, ev.clientY) - r.top;
        const x2 = Math.max(start.x, ev.clientX) - r.left;
        const y2 = Math.max(start.y, ev.clientY) - r.top;
        Object.assign(box.style, { left: x1 + 'px', top: y1 + 'px', width: x2 - x1 + 'px', height: y2 - y1 + 'px' });
        this.iconsLayer.querySelectorAll<HTMLElement>('.desktop-icon').forEach((el) => {
          const b = el.getBoundingClientRect();
          const hit = b.left - r.left < x2 && b.right - r.left > x1 && b.top - r.top < y2 && b.bottom - r.top > y1;
          el.classList.toggle('selected', hit);
        });
      };
      const up = () => {
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
        box.remove();
        if (active) this.swallowClick = true; // click po tažení rámečku by výběr zase zrušil
      };
      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
    });
    window.addEventListener('resize', debounce(() => { this.renderIcons(); this.renderTray(); }, 200));
    area.addEventListener('drop', (e) => {
      if ((e.target as HTMLElement).closest('iw-window')) return;
      const moved = e.dataTransfer?.getData('application/x-iwan-desktop');
      if (moved) {
        e.preventDefault();
        this.moveIcon(moved, this.cellAt(e.clientX, e.clientY));
        return;
      }
      const exp = e.dataTransfer?.getData('application/x-iwan-export');
      if (exp) { // jiná aplikace nabízí kopii (e-mail jako .eml) → složka Plocha
        e.preventDefault();
        void runExport(exp, this.boot.desktopFolderId).then(() => events.kick());
        return;
      }
      if (e.dataTransfer?.files.length) {
        e.preventDefault();
        void collectFromDrop(e.dataTransfer).then((c) => this.uploadFiles(c.files, this.boot.desktopFolderId, c.paths, c.dirs));
      }
    });
  }

  // ---------------------------------------------------------------- hlavní panel

  private buildTaskbar(): void {
    const startBtn = h('button', { type: 'button', class: 'tb-start', title: t('desktop.shell.start'), 'aria-haspopup': 'true' }, svg(IWAN_MARK, 'tb-logo'));
    startBtn.addEventListener('click', () => this.toggleStart(startBtn));
    const pinnedEl = h('div', { class: 'tb-pinned' });
    this.renderPinned(pinnedEl);
    const left = h('div', { class: 'tb-left' }, startBtn, pinnedEl, this.running);
    if (this.boot.session.kind === 'runas') {
      const endBtn = h('button', { type: 'button', class: 'btn sm' }, t('desktop.shell.run_as_end'));
      endBtn.addEventListener('click', () => void this.endRunAs());
      this.root.appendChild(h('div', { class: 'runas-bar' }, icon('switch-user'), h('span', null, t('desktop.shell.run_as_banner', { name: this.boot.user.name })), endBtn));
      const after = sessionStorage.getItem('iwan.openAfter');
      if (after) {
        sessionStorage.removeItem('iwan.openAfter');
        try {
          const o = JSON.parse(after) as { app: string; window: string };
          setTimeout(() => void this.openWindow(o.app, o.window), 600);
        } catch {
          /* ignore */
        }
      }
    }
    const user = h('button', { type: 'button', class: 'tb-user', title: this.boot.user.name }, h('span', { class: 'avatar sm' }, this.initials(this.boot.user.name)));
    user.addEventListener('click', () => IwMenu.showAt(this.userMenu(), user));
    this.clock.addEventListener('click', () => IwMenu.showAt(this.clockMenu(), this.clock));
    const right = h('div', { class: 'tb-right' }, this.tray, this.connIcon, this.clock, user);
    this.taskbar.replaceChildren(left, right);
    this.tickClock();
    window.setInterval(() => this.tickClock(), 15000);
    this.renderTray();
    this.watchBadges();
    contextMenu(this.taskbar, (e) => ((e.target as HTMLElement).closest('.tb-task, .tb-pin') ? null : this.taskbarMenu()));
  }

  private initials(name: string): string {
    return name.split(/\s+/).map((p) => p[0] ?? '').join('').slice(0, 2).toUpperCase();
  }

  private tickClock(): void {
    const now = new Date();
    this.clock.replaceChildren(h('span', { class: 'tb-time' }, fmtTime(now)), h('span', { class: 'tb-date' }, now.toLocaleDateString(currentLang() === 'cs' ? 'cs-CZ' : 'en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' })));
  }

  private renderPinned(el: HTMLElement): void {
    clear(el);
    for (const id of this.pinned) {
      const app = this.boot.apps.find((a) => a.id === id);
      if (!app || !app.main) continue;
      const main = app.main;
      const btn = h('button', { type: 'button', class: 'tb-pin', title: tk(app.name) }, icon(app.icon));
      btn.addEventListener('click', (e) => void this.openWindow(app.id, main, {}, e.shiftKey || e.ctrlKey));
      btn.addEventListener('auxclick', (e) => {
        if (e.button === 1) {
          e.preventDefault();
          void this.openWindow(app.id, main, {}, true);
        }
      });
      contextMenu(btn, () => [
        { label: t('desktop.shell.new_window'), icon: 'plus', onSelect: () => void this.openWindow(app.id, main, {}, true) },
        { label: tk(app.name), icon: app.icon, onSelect: () => void this.openWindow(app.id, main) },
        { label: t('desktop.shell.unpin'), icon: 'pin', onSelect: () => this.togglePin(app.id) },
      ]);
      el.appendChild(btn);
    }
  }

  private togglePin(appId: string): void {
    this.pinned = this.pinned.includes(appId) ? this.pinned.filter((p) => p !== appId) : [...this.pinned, appId];
    void savePref('pinned', this.pinned);
    const el = this.taskbar.querySelector<HTMLElement>('.tb-pinned');
    if (el) this.renderPinned(el);
  }

  private renderRunning(): void {
    clear(this.running);
    const groups = new Map<string, typeof this.wm.windows>();
    for (const w of this.wm.windows) {
      if (w.modal) continue;
      const key = this.groupTaskbar ? w.app : w.app + '#' + w.wid; // bez seskupení = tlačítko na každé okno
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)?.push(w);
    }
    const focused = this.wm.focusedWindow();
    for (const [key, wins] of groups) {
      const appId = key.split('#')[0] ?? key;
      const app = this.boot.apps.find((a) => a.id === appId);
      const active = wins.some((w) => w === focused || (focused?.modal && focused.parent === w));
      const btn = h(
        'button',
        { type: 'button', class: 'tb-task' + (active ? ' active' : '') + (wins.every((w) => w.state === 'minimized') ? ' minimized' : ''), title: wins.length === 1 ? wins[0]?.titleText() : tk(app?.name ?? appId) },
        icon(app?.icon ?? 'window'),
        this.wm.currentMode() !== 'mobile' ? h('span', { class: 'tb-task-label' }, wins.length === 1 ? wins[0]?.titleText() ?? '' : tk(app?.name ?? appId)) : null,
        wins.length > 1 ? h('span', { class: 'badge count' }, String(wins.length)) : null,
      );
      if (active && this.wm.currentMode() === 'mobile') window.requestAnimationFrame(() => btn.scrollIntoView({ inline: 'nearest', block: 'nearest' }));
      btn.addEventListener('auxclick', (e) => {
        if (e.button === 1 && app?.main) {
          e.preventDefault();
          void this.openWindow(app.id, app.main, {}, true);
        }
      });
      btn.addEventListener('click', () => {
        if (wins.length === 1) {
          const w = wins[0];
          if (!w) return;
          if (w.state === 'minimized') w.unminimize();
          else if (w === focused) w.minimize();
          else w.focusWindow();
        } else IwMenu.showAt(this.windowListMenu(wins), btn);
      });
      contextMenu(btn, () => [
        ...wins.map((w) => ({ label: w.titleText(), icon: 'window', onSelect: () => (w.state === 'minimized' ? w.unminimize() : w.focusWindow()) })),
        { separator: true, label: '' },
        app?.main ? { label: t('desktop.shell.new_window'), icon: 'plus', onSelect: () => void this.openWindow(app.id, app.main as string, {}, true) } : { label: '', separator: true },
        app?.main ? { label: t('desktop.shell.pin'), icon: 'pin', checked: this.pinned.includes(appId), onSelect: () => this.togglePin(appId) } : { label: '', separator: true },
        { label: t('desktop.shell.close_window'), icon: 'close', onSelect: () => wins.forEach((w) => void w.close()) },
      ]);
      this.running.appendChild(btn);
    }
  }

  private windowListMenu(wins: typeof this.wm.windows): MenuItem[] {
    return wins.map((w) => ({ label: w.titleText(), icon: 'window', onSelect: () => (w.state === 'minimized' ? w.unminimize() : w.focusWindow()) }));
  }

  private taskbarMenu(): MenuItem[] {
    return [
      { label: t('desktop.shell.show_desktop'), icon: 'desktop', onSelect: () => this.wm.minimizeAll() },
      { label: t('desktop.shell.close_all'), icon: 'close', onSelect: () => void this.wm.closeAll() },
      { separator: true, label: '' },
      { label: t('desktop.shell.task_manager'), icon: 'activity', onSelect: () => void this.openWindow('desktop', 'spravce_uloh') },
      { label: t('desktop.shell.personalize'), icon: 'palette', onSelect: () => this.personalize() },
    ];
  }

  private notificationItems(): MenuItem[] {
    const hist = toastHistory();
    return hist.length ? hist.slice(0, 15).map((r) => ({ label: r.text, icon: r.kind === 'ok' ? 'check' : r.kind === 'error' ? 'warning' : 'info' })) : [{ label: t('desktop.shell.no_notifications'), disabled: true }];
  }

  private badges = new Map<string, number>(); // odznaky aplikací v oznamovací oblasti (app → počet)
  private badgeMarkers = new Map<string, string | null>();
  private badgeTimers = new Map<string, number>();

  /** Odznaky aplikací: při startu a při události kanálu aplikace se zeptat její akce `badge` (počet + nové položky jako hlášky). */
  private watchBadges(): void {
    for (const app of this.boot.apps) {
      const b = app.tray?.badge;
      if (!b) continue;
      void this.refreshBadge(app.id);
      if (app.tray?.badgeChannel) {
        events.subscribe([app.tray.badgeChannel], () => {
          window.clearTimeout(this.badgeTimers.get(app.id));
          this.badgeTimers.set(app.id, window.setTimeout(() => void this.refreshBadge(app.id), 800));
        }, {});
      }
    }
  }

  private async refreshBadge(appId: string): Promise<void> {
    const app = this.boot.apps.find((a) => a.id === appId);
    const b = app?.tray?.badge;
    if (!app || !b) return;
    try {
      const r = await action<{ count: number; marker: string | null; items?: { text: string; always?: boolean; open?: { app: string; window: string; params?: Record<string, unknown> } }[] }>(app.id, b.window, b.action, {}, { since: this.badgeMarkers.get(appId) ?? null });
      const first = !this.badgeMarkers.has(appId); // po přihlášení jen hlášky s always (např. čeká na rozhodnutí správce)
      this.badgeMarkers.set(appId, r.marker);
      this.badges.set(appId, r.count);
      this.paintBadges();
      for (const it of r.items ?? []) if (!first || it.always) toast(it.text, 'info', 6000, it.open ? () => void this.wm.open({ app: it.open!.app, window: it.open!.window, params: it.open!.params ?? {} }) : undefined);
    } catch { /* odznak není důležitý – bez hlášky */ }
  }

  private paintBadges(): void {
    for (const btn of Array.from(this.tray.querySelectorAll<HTMLElement>('.tb-tray-btn[data-app]'))) {
      const n = this.badges.get(btn.dataset['app'] ?? '') ?? 0;
      btn.querySelector('.badge.app')?.remove();
      if (n > 0) btn.appendChild(h('span', { class: 'badge count app' }, String(Math.min(n, 99))));
    }
    const more = this.tray.querySelector<HTMLElement>('.tb-tray-more');
    if (more) {
      const total = Array.from(this.badges.values()).reduce((a, n) => a + n, 0);
      more.querySelector('.badge.app')?.remove();
      if (total > 0) more.appendChild(h('span', { class: 'badge count app' }, String(Math.min(total, 99))));
    }
  }

  private renderTray(): void {
    clear(this.tray);
    if (this.wm.currentMode() === 'mobile') { // mobil: místo v liště je drahé – jedna šipka, položky v nabídce
      const n = toastHistory().length;
      const more = h('button', { type: 'button', class: 'tb-tray-btn tb-tray-more', title: t('desktop.shell.tray') }, icon('chevron'), n ? h('span', { class: 'badge count' }, String(Math.min(n, 99))) : null);
      more.addEventListener('click', () => IwMenu.showAt([
        ...this.boot.apps.filter((a) => a.tray).map((a): MenuItem => ({ label: tk(a.name) + ((this.badges.get(a.id) ?? 0) > 0 ? ' (' + this.badges.get(a.id) + ')' : ''), icon: a.tray?.icon ?? 'window', onSelect: () => { if (a.tray?.window) void this.openWindow(a.id, a.tray.window); } })),
        { label: t('desktop.shell.notifications'), icon: 'bell', children: this.notificationItems() },
      ], more));
      this.tray.appendChild(more);
      this.paintBadges();
      return;
    }
    for (const app of this.boot.apps) {
      if (!app.tray) continue;
      const tray = app.tray;
      const btn = h('button', { type: 'button', class: 'tb-tray-btn', title: tk(app.name), dataset: { app: app.id } }, icon(tray.icon));
      btn.addEventListener('click', () => tray.window && void this.openWindow(app.id, tray.window));
      this.tray.appendChild(btn);
    }
    this.paintBadges();
    const n = toastHistory().length;
    const bell = h('button', { type: 'button', class: 'tb-tray-btn', title: t('desktop.shell.notifications') }, icon('bell'), n ? h('span', { class: 'badge count' }, String(Math.min(n, 99))) : null);
    bell.addEventListener('click', () => IwMenu.showAt(this.notificationItems(), bell));
    this.tray.appendChild(bell);
  }

  private setConnection(online: boolean): void {
    this.connIcon.replaceChildren(icon(online ? 'wifi' : 'wifi-off'));
    this.connIcon.hidden = online; // ukazuje se jen při problému
    this.connIcon.classList.toggle('ok', online);
    this.connIcon.classList.toggle('bad', !online);
    this.connIcon.title = online ? t('desktop.shell.connected') : t('desktop.shell.reconnecting');
    if (!online) toast(t('core.ui.offline'), 'warn');
    else toast(t('core.ui.online'), 'ok', 2000);
  }

  private clockMenu(): MenuItem[] {
    const now = new Date();
    return [{ label: now.toLocaleDateString(currentLang() === 'cs' ? 'cs-CZ' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }), icon: 'calendar', disabled: true }];
  }

  /** Správce: přihlásit se jako jiný uživatel v této záložce; vlastní token zůstane schovaný pro návrat. */
  private async runAs(openAfter?: { app: string; window: string }): Promise<void> {
    const r = await core<{ subjects: { users: { id: string; name: string }[] } }>('sharing', 'list', { type: 'storage', id: this.boot.desktopFolderId }).catch(() => null);
    const users = r?.subjects.users ?? [];
    if (!users.length) return;
    const sel = h('select', { class: 'input' }, users.map((u) => h('option', { value: u.id }, u.name)));
    const res = await dialog({ title: t('desktop.shell.run_as_user'), message: h('div', null, h('p', { class: 'muted small' }, t('desktop.shell.run_as_hint')), sel), buttons: [{ id: 'cancel', label: t('core.ui.cancel') }, { id: 'ok', label: t('desktop.shell.run_as_go'), primary: true }] });
    if (res.id !== 'ok') return;
    try {
      const out = await api<{ token: string }>('/auth/runas', { userId: sel.value });
      sessionStorage.setItem('iwan.parentToken', getToken() ?? '');
      if (openAfter) sessionStorage.setItem('iwan.openAfter', JSON.stringify(openAfter));
      setToken(out.token);
      location.reload();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  private async endRunAs(): Promise<void> {
    await api('/auth/runas-end', {}).catch(() => undefined);
    const parent = sessionStorage.getItem('iwan.parentToken');
    sessionStorage.removeItem('iwan.parentToken');
    setToken(parent || null);
    location.reload();
  }

  private userMenu(): MenuItem[] {
    return [
      { label: this.boot.user.name, icon: 'user', disabled: true },
      { separator: true, label: '' },
      { label: t('desktop.shell.personalize'), icon: 'palette', onSelect: () => this.personalize() },
      { label: t('desktop.shell.task_manager'), icon: 'activity', onSelect: () => void this.openWindow('desktop', 'spravce_uloh') },
      { label: t('desktop.shell.totp'), icon: 'lock', onSelect: () => void this.openWindow('desktop', 'dvoufazove') },
      { label: t('desktop.shell.app_passwords'), icon: 'shield', onSelect: () => void this.openWindow('desktop', 'hesla_aplikaci') },
      { label: t('desktop.shell.trips'), icon: 'globe', onSelect: () => void this.openWindow('desktop', 'cesty') },
      { label: t('desktop.about.title'), icon: 'info', onSelect: () => void this.openWindow('desktop', 'o_systemu') },
      ...(this.boot.user.isAdmin && this.boot.session.kind !== 'runas' ? [{ label: t('desktop.shell.run_as_user'), icon: 'switch-user', onSelect: () => void this.runAs() }] : []),
      { separator: true, label: '' },
      { label: t('desktop.shell.lock'), icon: 'lock', onSelect: () => void this.lock(true) },
      { label: t('desktop.shell.switch_user'), icon: 'switch-user', onSelect: () => this.handlers.switchUser() },
      { label: t('desktop.shell.logout'), icon: 'log-out', onSelect: () => void this.handlers.logout(false) },
      { label: t('desktop.shell.logout_forget'), icon: 'log-out', onSelect: () => void this.handlers.logout(true) },
    ];
  }

  // ---------------------------------------------------------------- Start

  private toggleStart(anchor: HTMLElement): void {
    if (this.startMenu) {
      this.closeStart();
      return;
    }
    const search = h('input', { class: 'input start-search', type: 'search', placeholder: t('desktop.shell.search'), 'aria-label': t('desktop.shell.search') });
    const grid = h('div', { class: 'start-grid' });
    // Start ve dvou oddílech: Aplikace a Správa (nastavení, uživatelé, audit, konzole) – koncepce A, krok 1
    const gridAdmin = h('div', { class: 'start-grid' });
    const adminHead = h('div', { class: 'start-section' }, t('desktop.shell.admin'));
    const renderApps = (filter: string) => {
      clear(grid);
      clear(gridAdmin);
      const q = filter.trim().toLowerCase();
      for (const app of this.boot.apps) {
        const wins = app.windows.filter((w) => w.inStart);
        for (const w of wins) {
          const label = wins.length > 1 || tk(w.title) !== tk(app.name) ? `${tk(app.name)} – ${tk(w.title)}` : tk(app.name);
          if (q && !label.toLowerCase().includes(q)) continue;
          const tile = h('button', { type: 'button', class: 'start-tile', title: label }, icon(w.icon ?? app.icon, 'icon big'), h('span', null, wins.length > 1 || w.startGroup === 'admin' ? tk(w.title) : tk(app.name))); // správa: název okna (Správa firmy), ne aplikace
          tile.addEventListener('click', () => {
            this.closeStart();
            void this.openWindow(app.id, w.id, {}, true); // Start otevírá vždy nové okno (jako Windows)
          });
          tile.addEventListener('auxclick', (e) => {
            if (e.button === 1) {
              e.preventDefault();
              this.closeStart();
              void this.openWindow(app.id, w.id, {}, true);
            }
          });
          contextMenu(tile, () => [
            { label: t('desktop.shell.open'), icon: 'folder-open', onSelect: () => void this.openWindow(app.id, w.id, {}, true) },
            { label: t('desktop.shell.create_shortcut'), icon: 'shortcut', onSelect: () => void this.createShortcut(app.id, w.id, {}, label, w.icon ?? app.icon) },
            app.main === w.id ? { label: this.pinned.includes(app.id) ? t('desktop.shell.unpin') : t('desktop.shell.pin'), icon: 'pin', onSelect: () => this.togglePin(app.id) } : { label: '', separator: true },
            ...(this.boot.user.isAdmin && this.boot.session.kind !== 'runas' ? [{ label: t('desktop.shell.run_as'), icon: 'switch-user', onSelect: () => void this.runAs({ app: app.id, window: w.id }) }] : []),
          ]);
          (w.startGroup === 'admin' ? gridAdmin : grid).appendChild(tile);
        }
      }
      adminHead.hidden = gridAdmin.hidden = !gridAdmin.childElementCount;
    };
    renderApps('');
    search.addEventListener('input', () => renderApps(search.value));
    search.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') ((grid.querySelector('button') ?? gridAdmin.querySelector('button')) as HTMLButtonElement | null)?.click();
      if (e.key === 'Escape') this.closeStart();
    });
    const power = h('button', { type: 'button', class: 'btn ghost', title: t('desktop.shell.logout') }, icon('power'));
    power.addEventListener('click', () => IwMenu.showAt([
      { label: t('desktop.shell.lock'), icon: 'lock', onSelect: () => void this.lock(true) },
      { label: t('desktop.shell.switch_user'), icon: 'switch-user', onSelect: () => this.handlers.switchUser() },
      { label: t('desktop.shell.logout'), icon: 'log-out', onSelect: () => void this.handlers.logout(false) },
    ], power));
    const menu = h(
      'div',
      { class: 'start-menu', role: 'dialog', 'aria-label': t('desktop.shell.start') },
      search,
      h('div', { class: 'start-section' }, t('desktop.shell.apps')),
      grid,
      adminHead,
      gridAdmin,
      h('div', { class: 'start-foot' }, h('span', { class: 'avatar sm' }, this.initials(this.boot.user.name)), h('span', { class: 'start-user' }, this.boot.user.name), power),
    );
    this.root.appendChild(menu);
    this.startMenu = menu;
    anchor.classList.add('active');
    const off = onOutsideClick(menu, () => this.closeStart(), anchor);
    menu.addEventListener('iw-close', off, { once: true });
    // na dotyku by zaměření vyvolalo klávesnici přes nabídku – hledání si uživatel klepne sám
    if (!window.matchMedia('(pointer: coarse)').matches) search.focus();
  }

  private closeStart(): void {
    if (!this.startMenu) return;
    this.startMenu.dispatchEvent(new Event('iw-close'));
    this.startMenu.remove();
    this.startMenu = null;
    this.taskbar.querySelector('.tb-start')?.classList.remove('active');
  }

  // ---------------------------------------------------------------- přizpůsobení

  private personalize(): void {
    const themeSel = h('select', { class: 'input' }, (['auto', 'light', 'dark'] as ThemePref[]).map((v) => h('option', { value: v, selected: currentTheme() === v }, t('desktop.shell.theme_' + v))));
    themeSel.addEventListener('change', () => {
      applyTheme(themeSel.value as ThemePref);
      void savePref('theme', themeSel.value);
    });
    const chromeSel = h('select', { class: 'input' }, (['bar', 'ribbon'] as const).map((v) => h('option', { value: v, selected: (this.boot.prefs['ui.chrome'] === 'ribbon' ? 'ribbon' : 'bar') === v }, t('desktop.shell.chrome_' + v))));
    chromeSel.addEventListener('change', () => { void savePref('ui.chrome', chromeSel.value); toast(t('desktop.shell.chrome_saved'), 'ok'); });
    const font = h('input', { type: 'range', min: '0.85', max: '1.5', step: '0.05', value: String(currentFontScale()) });
    font.addEventListener('input', () => applyFontScale(Number(font.value)));
    font.addEventListener('change', () => void savePref('fontScale', Number(font.value)));
    const aiOn = h('input', { type: 'checkbox', checked: this.boot.prefs['ai.enabled'] !== false });
    aiOn.addEventListener('change', () => { setAiOptOut(!aiOn.checked); void savePref('ai.enabled', aiOn.checked); toast(t('core.ui.saved'), 'ok'); });
    // přímé zápisy asistenta (kap. 34): osobní volba, správce zákazníka ji může zakázat (pak se nenabízí)
    const aiDirect = h('input', { type: 'checkbox', checked: this.boot.prefs['ai.direct'] === true });
    aiDirect.addEventListener('change', () => { void savePref('ai.direct', aiDirect.checked); toast(t('core.ui.saved'), 'ok'); });
    const wpColor = h('input', { type: 'color', value: '#5c001d' });
    const systemWp = h('div', { class: 'wp-tiles' }, ['vino', 'sede', 'modre', 'zelene', 'svetle'].map((name) => {
      const tile = h('button', { type: 'button', class: 'wp-tile', title: name, style: { backgroundImage: `url("${this.systemWallpaperUrl(name)}")` } });
      tile.addEventListener('click', () => {
        applyWallpaper({ url: this.systemWallpaperUrl(name), color: null });
        void savePref('wallpaper', { system: name, url: null, color: null });
      });
      return tile;
    }));
    const wpPick = h('button', { type: 'button', class: 'btn' }, t('desktop.shell.wallpaper_pick'));
    const wpNone = h('button', { type: 'button', class: 'btn ghost' }, t('desktop.shell.wallpaper_none'));
    wpColor.addEventListener('change', () => {
      const wp = { color: wpColor.value, url: null };
      applyWallpaper(wp);
      void savePref('wallpaper', wp);
    });
    wpPick.addEventListener('click', async () => {
      const picked = await pickFile({ mode: 'storage', accept: ['image/*'], defaultFolder: 'Obrázky', title: t('desktop.shell.wallpaper_pick') });
      const item = picked?.[0];
      if (item?.url) {
        applyWallpaper({ url: this.page.prefix + item.url, color: null });
        void savePref('wallpaper', { url: null, color: null, fileId: item.id });
      }
    });
    wpNone.addEventListener('click', () => {
      applyWallpaper(null);
      void savePref('wallpaper', null);
    });
    const posSel = h('select', { class: 'input' }, h('option', { value: 'bottom', selected: !this.root.classList.contains('taskbar-top') }, t('desktop.shell.bottom')), h('option', { value: 'top', selected: this.root.classList.contains('taskbar-top') }, t('desktop.shell.top')));
    posSel.addEventListener('change', () => {
      this.root.classList.toggle('taskbar-top', posSel.value === 'top');
      void savePref('taskbarTop', posSel.value === 'top');
      this.wm.windows.forEach((w) => w.apply());
    });
    const group = h('input', { type: 'checkbox', checked: this.groupTaskbar });
    group.addEventListener('change', () => {
      this.groupTaskbar = group.checked;
      void savePref('taskbarGroup', group.checked);
      this.renderRunning();
    });
    const center = h('input', { type: 'checkbox', checked: this.root.classList.contains('taskbar-center') });
    center.addEventListener('change', () => {
      this.root.classList.toggle('taskbar-center', center.checked);
      void savePref('taskbarCenter', center.checked);
    });
    const dlAsk = h('input', { type: 'checkbox', checked: this.boot.prefs['downloadConfirm'] !== false });
    dlAsk.addEventListener('change', () => void savePref('downloadConfirm', dlAsk.checked));
    const langSel = h('select', { class: 'input' }, this.page.langs.map((l) => h('option', { value: l, selected: l === currentLang() }, l === 'cs' ? 'Čeština' : l === 'en' ? 'English' : l)));
    langSel.addEventListener('change', async () => {
      await core('lang', 'set', { lang: langSel.value });
      location.reload();
    });
    const body = h(
      'div',
      { class: 'personalize' },
      h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('desktop.shell.theme')), themeSel),
      h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('desktop.shell.font')), font),
      h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('desktop.shell.chrome')), chromeSel),
      h('div', { class: 'field' }, h('span', { class: 'field-label' }, t('desktop.shell.wallpaper')), systemWp, h('div', { class: 'row' }, wpPick, wpNone)),
      h('div', { class: 'field' }, h('span', { class: 'field-label' }, t('desktop.shell.wallpaper_color')), h('div', { class: 'row' }, wpColor, h('span', { class: 'muted small' }, t('desktop.shell.wallpaper_color_help')))),
      h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('desktop.shell.taskbar_pos')), posSel),
      h('label', { class: 'check' }, center, h('span', null, t('desktop.shell.taskbar_center'))),
      h('label', { class: 'check' }, group, h('span', null, t('desktop.shell.taskbar_group'))),
      h('label', { class: 'check' }, dlAsk, h('span', null, t('desktop.shell.download_confirm'))),
      ...(aiInfo().enabled ? [h('label', { class: 'check' }, aiOn, h('span', null, t('desktop.shell.ai_enabled')))] : []),
      ...(aiInfo().enabled && aiInfo().directAllowed ? [h('label', { class: 'check' }, aiDirect, h('span', null, t('desktop.shell.ai_direct')))] : []),
      h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('desktop.shell.language')), langSel),
    );
    // Přizpůsobit je systémové okno (ne <dialog>), aby se okno „Vybrat soubor“ otevřelo před ním
    void this.wm.openLocal({ title: t('desktop.shell.personalize'), icon: 'palette', size: { w: 460, h: 600 }, build: () => ({ el: h('div', { class: 'view personalize-view' }, body) }) });
  }

  systemWallpaperUrl(name: string): string {
    return `${this.page.prefix}/assets/wallpapers/${name}.svg?v=${this.page.assetHash}`;
  }

  // ---------------------------------------------------------------- zámek, nečinnost, klávesy

  private installIdle(): void {
    const mark = () => {
      this.lastActivity = Date.now();
    };
    for (const ev of ['pointerdown', 'keydown', 'pointermove', 'wheel']) document.addEventListener(ev, mark, { passive: true });
    this.idleTimer = window.setInterval(() => {
      const minutes = this.boot.session.lockMinutes;
      if (minutes > 0 && !this.locked && Date.now() - this.lastActivity > minutes * 60000) void this.lock(true);
    }, 15000);
  }

  /**
   * Zvýšené ověření (odpověď 428): heslo a kód 2FA znovu, platí omezenou dobu. Vrátí true po úspěchu.
   * Stejný dialog pro všechna citlivá okna a příkazy (Správa serveru, …).
   */
  async elevate(): Promise<boolean> {
    const pwd = h('input', { class: 'input', type: 'password', autocomplete: 'current-password', required: true });
    const code = h('input', { class: 'input', type: 'text', inputmode: 'numeric', autocomplete: 'one-time-code', placeholder: '123 456', maxlength: '9' });
    const err = h('div', { class: 'field-help', style: { color: 'var(--danger)' } });
    const body = h('div', null,
      h('p', null, t('core.ui.elevate_text')),
      h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('core.ui.password')), pwd),
      h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('core.login.code')), code, h('div', { class: 'field-help' }, t('core.ui.elevate_code_help'))),
      err,
    );
    for (let attempt = 0; attempt < 3; attempt++) {
      const res = await dialog({ title: t('core.ui.elevate_title'), icon: 'shield', message: body, buttons: [{ id: 'cancel', label: t('core.ui.cancel') }, { id: 'ok', label: t('core.ui.verify'), primary: true }] });
      if (res.id !== 'ok') return false;
      try {
        await api('/auth/elevate', { password: pwd.value, code: code.value.replace(/\s+/g, '') }, { noElevate: true });
        return true;
      } catch (e) {
        err.textContent = tk((e as ApiError).message, (e as ApiError).params ?? undefined);
        pwd.value = '';
      }
    }
    return false;
  }

  async lock(tellServer: boolean): Promise<void> {
    if (this.locked) return;
    this.locked = true;
    this.closeStart();
    IwMenu.closeAll();
    if (tellServer) await api('/auth/lock', {}).catch(() => undefined);
    await lockScreen(this.boot.user, 'locked', () => this.handlers.switchUser());
    this.locked = false;
    this.lastActivity = Date.now();
    events.resume();
  }

  async relogin(): Promise<void> {
    if (this.locked) return;
    this.locked = true;
    this.closeStart();
    IwMenu.closeAll();
    await lockScreen(this.boot.user, 'relogin', () => this.handlers.switchUser());
    this.locked = false;
    events.resume();
    toast(t('core.ui.online'), 'ok');
  }

  private onKey(e: KeyboardEvent): void {
    if (isTyping(e.target)) return;
    // schránka na ploše (jen když není aktivní okno – to si zkratky řeší samo)
    if ((e.ctrlKey || e.metaKey) && !this.wm.focusedWindow()) {
      const sel = this.selectedItems();
      if (e.key.toLowerCase() === 'x' && sel.length) {
        e.preventDefault();
        setFileClipboard(sel.map((i) => i.id), true);
        return;
      }
      if (e.key.toLowerCase() === 'c' && sel.length) {
        e.preventDefault();
        setFileClipboard(sel.map((i) => i.id), false);
        return;
      }
      if (e.key.toLowerCase() === 'v' && fileClipboard()) {
        e.preventDefault();
        void pasteFileClipboard(this.boot.desktopFolderId);
        return;
      }
    }
    if (e.key === 'F5' && !e.ctrlKey && !this.wm.focusedWindow()) {
      e.preventDefault();
      void this.loadDesktopItems();
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      const btn = this.taskbar.querySelector<HTMLElement>('.tb-start');
      if (btn && !this.startMenu) this.toggleStart(btn);
    }
    if (e.key === 'Escape' && this.startMenu) this.closeStart();
  }

  private async restoreLayout(): Promise<void> {
    const key = 'layout.' + (this.wm.currentMode() === 'mobile' ? 'mobile' : 'pc');
    const entries = this.boot.prefs[key];
    if (Array.isArray(entries) && entries.length) {
      await this.wm.restoreLayout(entries as LayoutEntry[]);
    }
  }

  destroy(): void {
    this.unsubscribe?.();
    window.clearInterval(this.idleTimer);
    this.root.remove();
  }
}

export const debouncedSave = debounce((key: string, value: unknown) => void savePref(key, value), 500);
